import { ArtifactTypes } from '@mono/proto-clients';
import { artifactClient, projectDataClient, responseDocumentClient } from '../../../lib/connect/clients';
const FREEZABLE_KINDS = new Set([
    'review',
    'final',
    'named'
]);
/**
 * WRT-30: each volume's main ONLYOFFICE documents at their latest review /
 * final / named version, the versions a submission may freeze. Best effort: a
 * volume that cannot be read contributes nothing.
 */ export async function loadVolumeDocuments(projectId) {
    try {
        const { volumes } = await projectDataClient.listProjectVolumes({
            projectId
        });
        const perVolume = await Promise.all(volumes.map(async (vol)=>{
            try {
                const ws = await responseDocumentClient.getResponseWorkspace({
                    container: {
                        kind: 'volume',
                        projectId,
                        volumeId: vol.volumeId
                    }
                });
                const mains = ws.documents.filter((d)=>d.kind === 'document' && d.documentType === 'docx');
                const docs = await Promise.all(mains.map(async (d)=>{
                    const { versions } = await responseDocumentClient.listDocumentVersions({
                        container: {
                            kind: 'volume',
                            projectId,
                            volumeId: vol.volumeId
                        },
                        documentId: d.documentId,
                        pageSize: 200
                    });
                    const latest = versions.filter((v)=>FREEZABLE_KINDS.has(v.kind)).sort((a, b)=>b.versionNumber - a.versionNumber)[0];
                    if (!latest) return null;
                    return {
                        documentId: d.documentId,
                        label: d.label || 'Document',
                        volumeName: vol.displayName || vol.originalIdentifier || 'Volume',
                        versionId: latest.id,
                        versionNumber: latest.versionNumber,
                        versionLabel: latest.label,
                        kind: latest.kind
                    };
                }));
                return docs.filter((d)=>d !== null);
            } catch  {
                return [];
            }
        }));
        return perVolume.flat();
    } catch  {
        return [];
    }
}
/** Only persisted producer versions are offered; this surface never freezes or invents a baseline. */ export async function loadSubmissionSources(projectId, tenantId) {
    const [pricing, solicitation, outline, volumeDocuments] = await Promise.all([
        projectDataClient.listPricing({
            projectId
        }),
        projectDataClient.readSolicitationReview({
            projectId
        }),
        projectDataClient.getSectionMap({
            projectId
        }),
        loadVolumeDocuments(projectId)
    ]);
    const evidence = await Promise.all(pricing.scenarios.map((s)=>projectDataClient.listPricingEvidence({
            projectId,
            pricingId: s.id
        })));
    const flatten = (nodes)=>nodes.flatMap((n)=>[
                n,
                ...flatten(n.children)
            ]);
    const sectionVersions = await Promise.all(flatten(outline.sections).filter((s)=>s.source === 'user').map(async (s)=>{
        const result = await projectDataClient.listSectionVersions({
            projectId,
            sectionId: s.sectionId,
            limit: 100
        });
        return result.versions.filter((v)=>v.kind === 'review' || v.kind === 'milestone').map((v)=>({
                ...v,
                sectionId: s.sectionId,
                title: s.title
            }));
    }));
    const files = [];
    let total = 0;
    do {
        const page = await artifactClient.listArtifacts({
            tenantId,
            filter: {
                projectId
            },
            offset: files.length,
            limit: 100
        });
        total = Number(page.total);
        if (!page.artifacts.length) break;
        files.push(...page.artifacts);
    }while (files.length < total)
    return {
        scenarios: evidence.flatMap((e)=>e.versions.filter((v)=>v.frozen)),
        solicitations: solicitation.state?.versions ?? [],
        sections: sectionVersions.flat(),
        volumeDocuments,
        files: files.filter((f)=>f.checksumSha256 && f.storageKey && f.version > 0 && [
                ArtifactTypes.ArtifactStatus.UPLOADED,
                ArtifactTypes.ArtifactStatus.READY,
                ArtifactTypes.ArtifactStatus.PROCESSING,
                ArtifactTypes.ArtifactStatus.FAILED
            ].includes(f.status))
    };
}
